import type { GetStaticPaths } from "next"
import Image from "next/image"
import { useRouter } from "next/router"
import { Anchor } from "../../app/components/anchor"
import React from "react"
import { GoCheckCircle } from "react-icons/go"
import ReviewCard, { IReview } from "../../components/cards/ReviewCard"
import { ReviewsContextProvider } from "../../context/ReviewsContext"
import Navbar from "../../components/navbar"
import PageHead from "../../components/head/PageHead"
import Layout from "../../components/layout"
import BreedStarRating from "../../components/BreedStarRating"
import RatingCard from "../../components/review-rating-card"
import { PetBreedHeroForm } from "../../components/pet-breed/hero"
import Breadcrumb from "../../components/breadcrumbs"
import Coverage from "../../storyblok/static/Coverage"
import Cursor from "../../assets/vector/cursor-arrow-rays.svg"
import Badge from "../../assets/vector/check-badge.svg"
import Distance from "../../assets/vector/miles.svg"
import Map from "../../assets/vector/map.svg"
import Globe from "../../assets/vector/globe-americas.svg"
import Hero from "../../assets/images/military-moves-bg.png"
import data from "../../constants/military-bases/bases.json"
import militaryShipment from "../../constants/military-bases/shipment.json"
import transportationData from "../../constants/military-bases/transporters.json"
import reviews from "../../constants/military-bases/reviews.json"
import { urlifyComplexName } from "../../constants/stateCityDictionary"
import Footer from "../../storyblok/static/Footer"
import { shuffleArray } from "../../util/array"
import { localizeNumber } from "../../util/formatNumber"
import { getCityUrl } from "../../util/getCityUrl"
import { redactNames } from "../../util/string"
import { isValidDriverId } from "@/util/validateDriver"
import { safelyRenderHtml } from "@/util/safelyRenderHtml"
import LazyYoutube from "@/components/testimonials/LazyYoutube"

const navbarActiveButtons = (shipmentFormLink: string) => [
  {
    text: "Get Transportation Quotes",
    route: shipmentFormLink,
  },
]
type BaseData = {
  base: string
  title: string
  description: string | number
  content: string
  serviceBranch: string
  "Program/Service": string
  fleetTitle: string
  address: string
  "Phone/Fax 1": string
  "Phone/Fax 2": string
  "Phone/Fax 3": string
  "Phone/Fax 4": string
  "Phone/Fax 5": string
  "Phone/Fax 6": string
  website: string
  additionalInformation: string
  map: string
}
const navbarSideButtons = [
  {
    text: "How it works",
    route: "/how-it-works",
  },
  {
    text: "Log in",
    route: "/login",
    isReactLink: true,
  },
]

const tableHeadings = ["Shipment", "Location", "Distance", "Cost"]

const MilitaryBase = ({
  bases,
  otherBases,
  shipments,
  transporters,
  content,
  reviewsData,
}: {
  bases: BaseData
  otherBases: BaseData[]
  shipments: { [key: string]: string }[]
  transporters: { [key: string]: string }[]
  content: string
  reviewsData: IReview[]
}) => {
  const router = useRouter()
  const { base } = router.query

  if (!base) {
    return <div>Loading...</div>
  }

  const pageTitle = bases.title
  const pageDescription = bases.description
  const headerContent = {
    title: pageTitle,
    metaTags: [
      {
        id: 1,
        content: pageDescription,
        name: "description",
      },
    ],
    showZendeskHelp: false,
  } as any

  const splitHTMLContent = (html: string) => {
    const openTag = "<h3>"
    const closeTag = "</h3>"
    let startIndex = 0
    let endIndex = 0
    let thirdH3Content = ""
    let count = 0

    while (count < 3) {
      const openIndex = html.indexOf(openTag, endIndex)
      if (openIndex === -1) break
      endIndex = html.indexOf(closeTag, openIndex)
      if (endIndex === -1) break
      endIndex += closeTag.length
      count++

      if (count === 3) {
        startIndex = openIndex + openTag.length
        thirdH3Content = html.substring(startIndex, endIndex - closeTag.length)
      }
    }

    const firstContentInitial = html.substring(0, startIndex - openTag.length)
    const firstContent =
      firstContentInitial +
      "\n<p>For instructions on how you can prepare for your upcoming trip and how to fill out the <Anchor href='/military-pet-reimbursement-form'>pet PCS reimbursement form DD Form 1351-2</Anchor>, do not hesitate to contact us.</p>"

    const lastContent = html.substring(endIndex)

    return [firstContent, lastContent, thirdH3Content]
  }

  const [firstContent, lastContent, thirdH3Content] = splitHTMLContent(content)

  return (
    <ReviewsContextProvider>
      <PageHead title={pageTitle} description={String(pageDescription)} />
      <Layout {...headerContent}>
        <div>
          <section className="bg-flag bg-cover bg-no-repeat bg-center">
            <Navbar
              activeButton={navbarActiveButtons("/shipments/new/animals")}
              sideButton={navbarSideButtons}
              fixedNavigation
            />
            <section id="page-hero" className="pt-12 md:pt-20">
              <div className="flex gap-8 lg:gap-14 max-w-[1289px] mx-auto pb-10 mt-10 items-center">
                <div className="w-full md:w-2/5">
                  <div className="pb-8">
                    <Breadcrumb
                      items={[
                        { name: "Home", href: "/" },
                        { name: "PCS Relocation", href: "/military-moves" },
                        {
                          name: "All Bases",
                          href: "/military-moves/all-bases",
                        },
                        { name: bases.base, href: "/all-bases" },
                      ]}
                    />
                  </div>
                  <div className="h-fit bg-white p-6 rounded-xl space-y-6 drop-shadow">
                    <h1 className="m-0 text-blue7 text-2xl lg:text-[42px] lg:leading-[52px] font-semibold font-montserrat">
                      <span className="capitalize"></span> {bases.base} pet
                      relocation service
                    </h1>
                    <h2 className="text-sm sm:text-md xl:text-lg font-normal text-gray9">
                      The easy, safe and stress-free way to transport your pets.
                      Within minutes of posting a listing on our online
                      marketplace, you'll receive free quotes from caring{" "}
                      <Anchor
                        className="underline text-gray9"
                        href="/pet-transportation"
                      >
                        pet transport experts
                      </Anchor>{" "}
                      dedicated to excellence. Find trusted drivers in just
                      minutes!
                    </h2>
                    <PetBreedHeroForm hasPickup={false} hasDelivery={false} />
                    <div dir="ltr">
                      <aside className="mt-3 sm:mt-6">
                        <RatingCard isMilitary />
                      </aside>
                    </div>
                  </div>
                </div>
                <div className="hidden md:block md:w-3/5">
                  <Image
                    className="w-[690px] h-[593px] lg:w-auto lg:h-auto object-cover object-top"
                    src={Hero.src}
                    alt={"military-moves"}
                    height={613}
                    width={711}
                    priority
                  />
                </div>
              </div>
            </section>
          </section>
          <section
            id="stats"
            className="mt-10 md:mt-10 pt-12 max-w-[1289px] mx-auto px-4 md:px-1"
          >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-x-4 gap-y-8 mb-4 max-w-[1350px] mx-auto">
              <div className="flex items-center">
                <Image
                  src={Globe.src}
                  alt={"rating"}
                  height={48}
                  width={48}
                  loading="lazy"
                />
                <div className="pl-2">
                  <p className="mb-0.5">1600+ members</p>
                  <span className="text-sm">
                    former military members participated as drivers on
                    CItizenShipper
                  </span>
                </div>
              </div>
              <div className="flex items-center">
                <Image
                  src={Cursor.src}
                  alt={"rating"}
                  height={48}
                  width={48}
                  loading="lazy"
                />
                <div className="pl-2">
                  <p className="mb-0.5">90% of listings</p>
                  <span className="text-sm">
                    Receive a quote in less than 10 min.
                  </span>
                </div>
              </div>
              <div className="flex items-center">
                <Image
                  src={Map.src}
                  alt="rating"
                  width={48}
                  height={48}
                  loading="lazy"
                />
                <div className="pl-2">
                  <p className="mb-0.5">100,000,000+</p>
                  <span className="text-sm">
                    Miles driven by qualified transporters.
                  </span>
                </div>
              </div>
              <div className="flex items-center">
                <Image
                  src={Badge.src}
                  alt={"rating"}
                  height={48}
                  width={48}
                  loading="lazy"
                />
                <div className="pl-2">
                  <p className="mb-0.5">14+ years experience</p>
                  <span className="text-sm">Shipping what matters most.</span>
                </div>
              </div>
            </div>
          </section>
          {!!shipments.length && (
            <section
              id="table"
              className="mt-6 max-w-[1289px] mx-auto px-4 md:px-1 mb-10 pb-10"
            >
              <div className="overflow-x-auto">
                <div className="max-auto md:text-center w-full mb-7 mt-10 pt-8">
                  <h2 className="font-semibold mb-6 font-montserrat md:text-[33px] md:leading-11 ">
                    Successful {bases.base} transports from our marketplace
                  </h2>
                  <p className="mb-4 text-center mx-auto max-w-4xl">
                    Every military relocation is different — from the distance
                    of the move, to the size of your family, and whether or not
                    you have children or need{" "}
                    <Anchor
                      className="text-gray9 underline"
                      href="/military-moves"
                    >
                      military pet relocation
                    </Anchor>{" "}
                    services.
                  </p>
                  <p className="mb-4 text-center mx-auto max-w-4xl">
                    Your pet is so much more than four legs and a tail — they're
                    members of your family and best friends for life.
                  </p>
                  <p className="mb-6 text-center mx-auto max-w-4xl">
                    Our unique process matches you with a fleet of pre-screened
                    drivers with prior experience in military pet relations,
                    with strong emphasis on your pet's comfort and safety.
                  </p>
                </div>
                <table className="min-w-full bg-white pb-2">
                  <thead>
                    <tr className="w-full h-11 text-base border-gray-300 py-8 bg-blue26">
                      {tableHeadings.map((item) => (
                        <th
                          key={item}
                          className="uppercase text-left font-medium text-gray-600 px-2 md:px-6 py-2 text-sm md:text-base"
                        >
                          {item}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {shipments.map((shipment) => (
                      <tr
                        className="h-11 border-gray-300 border-b"
                        key={shipment.id}
                      >
                        <td className="text-left px-1 md:px-6 py-2 text-sm md:text-base">
                          {redactNames(shipment.shipper_name ?? "")} shipped{" "}
                          <span className="font-bold">{shipment.pet_name}</span>
                        </td>
                        <td className="text-left px-1 md:px-6 py-2 text-sm md:text-base">
                          {getCityUrl(shipment.location_from) ? (
                            <Anchor
                              href={getCityUrl(shipment.location_from) ?? ""}
                            >
                              {shipment.location_from}
                            </Anchor>
                          ) : (
                            shipment.location_from
                          )}{" "}
                          →{" "}
                          {getCityUrl(shipment.location_to) ? (
                            <Anchor
                              href={getCityUrl(shipment.location_to) ?? ""}
                            >
                              <b>{shipment.location_to}</b>
                            </Anchor>
                          ) : (
                            <b>{shipment.location_to}</b>
                          )}
                        </td>
                        <td className="text-left px-1 md:px-6 py-2">
                          <span className="bg-blue16 rounded-full px-1.5 py-0.5 md:px-2 md:py-2 text-xs md:text-base inline-flex items-center">
                            <Image
                              src={Distance.src}
                              alt={"rating"}
                              height={26}
                              width={24}
                              loading="lazy"
                              className="w-[28px] h-[28px]"
                            />
                            {shipment.distance.toLocaleString()} mi
                          </span>
                        </td>
                        <td className="text-left px-1 md:px-6 py-4 text-blue7 text-sm md:text-base">
                          {localizeNumber(shipment.bid_amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
          <Coverage />
          {!!transporters.length && (
            <section
              id="transporters"
              className="mt-6 max-w-[1289px] mx-auto px-4 md:px-1"
            >
              <h3 className="max-w-[40ch] pt-16 mx-auto mb-16 font-montserrat font-medium text-3xl md:text-4xl leading-[44px] text-center text-blue7">
                We are committed to building a safe, caring environment for
                people and animals on the road
              </h3>
              <div className="grid lg:grid-cols-[446px_440px] gap-4 md:gap-6 lg:gap-11 max-auto justify-center">
                <div className="max-w-[446px]">
                  <div className="border-gray52 border rounded">
                    <h4 className="p-4 bg-blue16 flex items-center">
                      Top transporters in {bases.base}
                    </h4>

                    {transporters.map(
                      ({
                        photo_url,
                        transporter_name,
                        id,
                        avg_rating,
                        transporter_id,
                      }) => {
                        const roundedValue =
                          Math.round(Number(avg_rating) * 10) / 10
                        return (
                          <div
                            className="p-6 border-b last-of-type:border-none flex"
                            key={id}
                          >
                            <Image
                              src={photo_url}
                              alt={`${id} reviews`}
                              className="h-14 w-14 mr-4 rounded-full overflow-hidden object-cover"
                              width={56}
                              height={56}
                            />
                            <div>
                              <p className="mb-0 text-lg">
                                {isValidDriverId(transporter_id) ? (
                                  <Anchor
                                    href={`https://citizenshipper.com/drivers/${transporter_id}`}
                                  >
                                    {transporter_name}
                                  </Anchor>
                                ) : (
                                  transporter_name
                                )}
                              </p>
                              <div className="flex items-center text-sm">
                                <BreedStarRating value={Number(avg_rating)} />
                                <span className="ml-3">{roundedValue}/5</span>
                              </div>
                            </div>
                          </div>
                        )
                      },
                    )}
                  </div>
                </div>
                <div className="">
                  <ul className="px-0">
                    <li className="text-xl py-3 leading-7 flex items-center">
                      <span className="text-green5 pr-6">
                        <GoCheckCircle size={36} />
                      </span>{" "}
                      We carefully background check all drivers.
                    </li>
                    <li className="text-xl py-3 leading-7 flex items-center">
                      <span className="text-green5 pr-6">
                        <GoCheckCircle size={36} />
                      </span>{" "}
                      We verify transporter addresses and contact information
                      for additional protection.
                    </li>
                    <li className="text-xl py-3 leading-7 flex items-center">
                      <span className="text-green5 pr-6">
                        <GoCheckCircle size={36} />
                      </span>{" "}
                      Photos of drivers and their vehicles help confirm who and
                      what to expect.
                    </li>
                    <li className="text-xl py-3 leading-7 flex items-center">
                      <span className="text-green5 pr-6">
                        <GoCheckCircle size={36} />
                      </span>{" "}
                      Detailed customer reviews provide insight and transparency
                      to your selection process.
                    </li>
                    <li className="mt-10">
                      {" "}
                      <Anchor
                        href={"/shipments/new/animals"}
                        className="w-full md:w-auto px-4 md:px-16 rounded-md py-5 text-center bg-blue7 hover:text-white hover:no-underline text-white text-lg"
                      >
                        Get Pet Transportation Quotes
                      </Anchor>
                    </li>
                  </ul>
                </div>
              </div>
            </section>
          )}

          <section
            id="article"
            className="mx-auto pt-10 mt-10 px-4 md:px-4 max-w-[1289px] mb-12 pb-10 "
          >
            <div className="w-full h-full md:w-5/6 md:min-h-[540px] p-2 my-8 mx-auto">
              <div className="md:p-2 rounded-md border border-color-gold shadow-sm">
                <LazyYoutube videoId="E74w3PNkQ2s" title="Cost to Ship Video" />
              </div>
            </div>
            <div className="prose-h2:text-center prose-h2:font-montserrat prose-h2:text-blue7 prose-h4:mb-2 prose-h4:pt-2 prose-h4:font-semibold prose-h4:text-base prose-li:mb-4 prose-h2:mb-8 prose-h2:leading-10 prose-h3:text-xl prose-h3:font-bold prose-h3:mt-10 prose-a:underline prose-a:text-black prose-ul:list-disc prose-table:w-full prose-table:pt-4 prose-table:mt-4 prose-table:border-b prose-tr:bg-white prose-tr:px-2  prose-tr:even:bg-gray-300 prose-th:bg-blue26 prose-tr:border-b prose-th:uppercase prose-th:text-gray-600 prose-th:font-medium prose-th:px-4 prose-th:py-2 prose-td:px-3 prose-td:py-2 prose-ol:list-decimal">
              {safelyRenderHtml(firstContent)}
            </div>
            <div className="prose-h2:text-center prose-h2:font-montserrat prose-h2:text-blue7 prose-h4:mb-2 prose-h4:pt-2 prose-h4:font-semibold prose-h4:text-base prose-li:mb-4 prose-h2:mb-8 prose-h2:leading-10 prose-h3:text-xl prose-h3:font-bold prose-h3:mt-10 prose-a:underline prose-a:text-black prose-ul:list-disc prose-table:w-full prose-table:pt-4 prose-table:mt-4 prose-table:border-b prose-tr:bg-white prose-tr:px-2  prose-tr:even:bg-gray-300 prose-th:bg-blue26 prose-tr:border-b prose-th:uppercase prose-th:text-gray-600 prose-th:font-medium prose-th:px-4 prose-th:py-2 prose-td:px-3 prose-td:py-2 prose-ol:list-decimal">
              <h2>Will The Military Pay for Pet PCS?</h2>
              <p>
                When you receive a PCS, you may be worried about the financial
                burden of moving household items, vehicles and even pet
                transportation costs.{" "}
                <Anchor href="https://citizenshipper.com/blog/pcs-pet-transportation-allowance/">
                  Moving with pets for military families
                </Anchor>{" "}
                is just one more layer of stress. Will the military help out
                with pet relocation costs? Yes, they will!
              </p>
              <p>
                "As of January 1, 2024, military Service members going through a
                PCS (permanent change of station orders) within the continental
                United States can be reimbursed up to $550 for one household
                pet, either cat or dog, and up to $2,000 for moves to or from a
                location outside the continental United States to cover costs
                related to the transportation of a pet."
              </p>
              <p>
                The new policy regarding reimbursement for PCS pet relocation
                services was{" "}
                <Anchor href="https://www.defense.gov/News/Releases/Release/Article/3434402/department-of-defense-approves-policy-to-reimburse-pet-expenses-due-to-a-perman/">
                  approved by the Department of Defense
                </Anchor>{" "}
                in June 2023.
              </p>
              <p>
                If you are traveling to or from certain high-risk countries, the
                Defense Department has authorized reimbursement of up to $4,000
                starting November 25, 2024. This policy is in response to the{" "}
                <Anchor href="https://www.cdc.gov/importation/dogs/index.html">
                  Centers for Disease Control and Prevention (CDC)
                </Anchor>{" "}
                implementing a regulation that requires a higher screening level
                for dogs returning to the United States from{" "}
                <Anchor href="https://www.travel.dod.mil/Portals/119/Documents/JTR/Supplements/pet_allowance_locations.pdf">
                  countries that pose a high risk of rabies
                </Anchor>
                .
              </p>
              <p>
                It's important to note that this increased allowance is only
                granted when the Patriot Express capacity and regularly
                scheduled commercial airline service are not available for pets.
              </p>
            </div>
            <section className="relative sm:max-h-[130vh] my-12  py-12 overflow-hidden">
              <h3 className="font-montserrat text-lg md:text-4xl text-center text-blue7 mb-6  md:mb-10">
                See what CitizenShipper customers have to say
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {reviewsData.slice(0, 1).map((review) => (
                  <ReviewCard review={review} key={review.reviewer} />
                ))}

                {reviewsData.slice(2, 3).map((review) => (
                  <ReviewCard review={review} key={review.reviewer} />
                ))}
              </div>
              <div className="hidden md:block absolute bottom-0 left-0 w-full h-2/5 bg-gradient-to-b from-transparent to-white"></div>
            </section>
            <h3 className="text-xl font-bold mt-10">
              {safelyRenderHtml(thirdH3Content)}
            </h3>
            <div className="prose-h2:text-center prose-h2:text-blue7 prose-h4:mb-2 prose-h4:pt-2 prose-h4:font-semibold prose-h4:text-base prose-li:mb-4 prose-h2:mb-8 prose-h2:leading-10 prose-h3:text-xl prose-h3:font-bold prose-h3:mt-10 prose-a:underline prose-a:text-black prose-ul:list-disc prose-table:w-full prose-table:pt-4 prose-table:mt-4 prose-table:border-b prose-tr:bg-white prose-tr:px-2  prose-tr:even:bg-gray-300 prose-th:bg-blue26 prose-tr:border-b prose-th:uppercase prose-th:text-gray-600 prose-th:font-medium prose-th:px-4 prose-th:py-2 prose-td:px-3 prose-td:py-2 prose-ol:list-decimal">
              {safelyRenderHtml(lastContent)}
            </div>
          </section>
          <section
            id="ready-to-ship"
            className="p-4 md:text-center max-w-[1145px] mx-auto"
          >
            <h5 className="text-3xl md:text-4xl font-medium font-montserrat">
              Ready for your PCS pet relocation?
            </h5>
            <p className="mt-4 mb-12 text-lg max-w-xl md:text-center w-full mx-auto">
              No hidden costs or upcharges. Just a simple and stress-free
              experience. Get started in two minutes or less!
            </p>
            <Anchor
              href={"/shipments/new/animals"}
              className="w-full md:w-auto px-8 md:px-16 rounded-md py-5 text-center bg-blue7 hover:bg-blue-700 mt-5 text-white hover:text-white text-md hover:no-underline"
            >
              Get PCS Pet Relocation Quotes
            </Anchor>
          </section>
          <section
            id="other-bases"
            className="mx-auto pt-10 mt-10 px-4 md:px-4 max-w-[1289px] mb-12 pb-10"
          >
            <h3 className="text-center text-lg mt-4 md:mt-12 mb-4 md:mb-10 md:text-4xl font-montserrat">
              Other {bases.serviceBranch} PCS Pet Relocation Services
            </h3>
            <ul className="grid md:grid-cols-3 gap-x-6 gap-y-8 px-0 list-none">
              {otherBases?.map((item) => (
                <li key={item.base} className="text-left px-0">
                  <Anchor
                    href={`/military-moves/${urlifyComplexName(item.base)}`}
                  >
                    {item.base}
                  </Anchor>
                </li>
              ))}
            </ul>
          </section>
          <Footer />
        </div>
      </Layout>
    </ReviewsContextProvider>
  )
}
export const getStaticPaths: GetStaticPaths = async () => {
  const paths = data.map((baseData: { base: string }) => ({
    params: { base: urlifyComplexName(baseData.base) },
  }))

  return { paths, fallback: "blocking" }
}

export const getStaticProps = async ({
  params,
}: {
  params: { base: string }
}) => {
  try {
    const { base } = params
    const reviewsData = shuffleArray(
      reviews.map((item) => ({
        reviewer: item.reviewer,
        rating: item.rating,
        content: item.content,
      })),
    )
    let otherBases: BaseData[] = []
    let shipments = []
    let transporters = []
    let content = ""
    const bases = data.find(
      (baseData: { base: string }) => urlifyComplexName(baseData.base) === base,
    )
    if (bases) {
      otherBases = data
        .filter((item) => item.serviceBranch === bases?.serviceBranch)
        .filter((item) => item.base != bases.base)

      shipments = shuffleArray(
        militaryShipment.filter(
          (item) => urlifyComplexName(item.installation) === base,
        ),
      ).slice(0, 5)
      transporters = shuffleArray(
        transportationData.filter(
          (item) => urlifyComplexName(item.installation) === base,
        ),
      ).slice(0, 5)
      content = bases.content
      return {
        props: {
          bases,
          otherBases,
          shipments,
          transporters,
          content,
          reviewsData,
        },
      }
    }

    return {
      notFound: true,
    }
  } catch (err) {
    console.error("Error fetching military moves data:", err)
    return { notFound: true }
  }
}
export default MilitaryBase
